import {
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  h,
} from '@stencil/core';
import { HTMLStencilElement, Prop, State, Watch } from '@stencil/core/internal';
import { Constants, Language } from '../../enums/const-enums';
import { getCookieValue, logd } from '../../utils/utils';
import {
  RassEmailTranslations,
  rassEmailTranslations,
} from './i18n/rass-email.i18n';

@Component({
  tag: 'rass-email',
  styleUrl: 'rass-email.scss',
  shadow: false,
})
export class RassEmailComponent {
  @Element() element!: HTMLStencilElement;
  @Prop() language: Language;
  @Prop() isBlockingUserInput = false;
  @Prop() hasServerError: boolean;
  @Prop({ mutable: true }) emailAddress: string;
  @State() isEmailAddressValid: boolean;
  @Event({ eventName: 'rass:email:submit' }) submitEvent: EventEmitter<string>;

  //https://hub.rabobank.nl/rop-portal/docs/rds/rds-components/input-email-0a49de4d-e6e9-3707-8f8c-76d11b780f5b/
  emailPattern =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  componentWillLoad(): void {
    if (!this.emailAddress) {
      this.emailAddress = getCookieValue(Constants.EMAIL_COOKIE);
    }
  }

  componentDidLoad(): void {
    if (this.emailAddress) {
      this.rdsInputElem
        .validate()
        .then((isValid: boolean) => {
          this.isEmailAddressValid = isValid;
        })
        .catch((err: Error) => logd(() => console.error(err)));
    }
    setTimeout(() => {
      this.rdsInputElem
        .setFocus()
        .catch((err: Error) => logd(() => console.error(err)));
    }, 0);
  }

  @Watch('hasServerError')
  statusTextChanged(isNewValue: boolean): void {
    if (isNewValue) {
      this.rdsInputElem
        .validate()
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleInput(): void {
    this.emailAddress = this.rdsInputElem.value.toLowerCase();
    this.isEmailAddressValid = false;

    if (this.emailAddress.match(this.emailPattern)) {
      this.isEmailAddressValid = true;
      this.rdsInputElem
        .validate()
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleEnterKey(event: CustomEvent<KeyboardEvent>): void {
    if (event.detail.key === 'Enter') {
      this.rdsInputElem
        .validate()
        .then((isValid) => {
          if (isValid) {
            this.handleSubmit();
          }
        })
        .catch((err: Error) => logd(() => console.error(err)));
    }
  }

  handleSubmit(): void {
    this.rdsInputElem
      .validate()
      .then((isValid: boolean) => {
        if (isValid) {
          this.submitEvent.emit(this.emailAddress);
        }
      })
      .catch((err: Error) => logd(() => console.error(err)));
  }

  get rdsInputElem(): HTMLRdsInputElement {
    return this.element.querySelector<HTMLRdsInputElement>('rds-input-email');
  }

  get translations(): RassEmailTranslations {
    return (
      rassEmailTranslations[this.language] || rassEmailTranslations[Language.EN]
    );
  }

  render(): void {
    return (
      <Host>
        <rds-input-wrapper>
          <rds-input-email
            id="email-input"
            label={this.translations.inputLabel}
            name="rass-email-input"
            slot="input"
            placeholder={this.translations.inputPlaceholder}
            disabled={this.isBlockingUserInput}
            onRdsInput={(): void => this.handleInput()}
            onRdsKeyDown={(event): void => this.handleEnterKey(event)}
            value={this.emailAddress}
            required
          ></rds-input-email>
          <custom-error slot="error" for="rass-email-input" when="valueMissing">
            {this.translations.valueMissing}
          </custom-error>
          <custom-error slot="error" for="rass-email-input" when="typeMismatch">
            {this.translations.typeMismatch}
          </custom-error>
          <custom-error
            slot="error"
            for="rass-email-input"
            when="patternMismatch"
          >
            {this.translations.patternMismatch}
          </custom-error>
        </rds-input-wrapper>
        <rds-button
          id="email-request"
          disabled={!this.isEmailAddressValid || this.isBlockingUserInput}
          onRdsClick={(): void => this.handleSubmit()}
        >
          {this.translations.buttonLabel}
        </rds-button>
      </Host>
    ) as void;
  }
}
